import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Collapse,
  Alert,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { IconChevronUp, IconChevronDown, IconX, IconPhotoPlus } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MediaDropzone from './MediaDropzone';
import MediaPreview from './MediaPreview';

const MediaUploadSection = ({ 
  mediaFiles = [], 
  onMediaFilesChange,
  maxFiles = 8, // Threads API 제한으로 변경
  disabled = false,
  compact = false, // 컴팩트 모드 (드롭존 숨김)
  uploadProgressMap = {}, // 업로드 진행률 맵
  variant = 'default',
  showConnectionLine = true
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true); // 항상 펼쳐진 상태로 시작
  const [error, setError] = useState(null);
  const isInlineVariant = variant === 'inline';

  // 미디어 파일 추가
  const handleMediaAdd = useCallback((newFiles) => {
    if (disabled) return;

    setError(null);
    
    // 중복 파일 검사
    const existingFileNames = mediaFiles.map(f => f.name);
    const uniqueFiles = newFiles.filter(file => !existingFileNames.includes(file.name));
    
    if (uniqueFiles.length !== newFiles.length) {
      setError(t('postThreads.media.messages.duplicateFiles'));
    }
    
    // 비디오 파일 크기 검사 (300MB 제한)
    const MAX_VIDEO_SIZE = 300 * 1024 * 1024; // 300MB
    const oversizedVideos = uniqueFiles.filter(file => 
      file.type.startsWith('video/') && file.size > MAX_VIDEO_SIZE
    );
    
    if (oversizedVideos.length > 0) {
      const fileNames = oversizedVideos.map(f => f.name).join(', ');
      setError(t('postThreads.media.videoSizeExceeded', { fileNames }));
      // 크기 초과 파일 제외
      const validFiles = uniqueFiles.filter(file => 
        !file.type.startsWith('video/') || file.size <= MAX_VIDEO_SIZE
      );
      uniqueFiles.length = 0;
      uniqueFiles.push(...validFiles);
    }
    
    // 최대 파일 수 검사
    const totalFiles = mediaFiles.length + uniqueFiles.length;
    if (totalFiles > maxFiles) {
      const allowedCount = maxFiles - mediaFiles.length;
      const limitedFiles = uniqueFiles.slice(0, allowedCount);
      setError(t('postThreads.media.maxFilesExceeded', { maxFiles }));
      onMediaFilesChange([...mediaFiles, ...limitedFiles]);
      return;
    }
    
    // 파일 객체와 메타데이터를 분리하여 보관
    const filesWithPreview = uniqueFiles.map((file, index) => {
      const fileWrapper = {
        file: file, // 원본 File 객체를 별도 프로퍼티로 보존
        id: `${Date.now()}-${index}-${Math.random()}`,
        preview: file instanceof File ? URL.createObjectURL(file) : null,
        // 메타데이터 (File 객체 외부에 보관)
        name: file.name || t('postThreads.media.file', { index: index + 1 }),
        type: file.type || 'application/octet-stream',
        size: file.size || 0
      };
      
      console.log('📋 File wrapper created:', {
        id: fileWrapper.id,
        name: fileWrapper.name,
        size: fileWrapper.size,
        hasFileObject: fileWrapper.file instanceof File
      });
      
      return fileWrapper;
    });
    
    onMediaFilesChange([...mediaFiles, ...filesWithPreview]);
  }, [mediaFiles, onMediaFilesChange, maxFiles, disabled, t]);

  // 미디어 파일 제거
  const handleMediaRemove = useCallback((index) => {
    if (disabled) return;
    
    const updatedFiles = [...mediaFiles];
    const removedFile = updatedFiles.splice(index, 1)[0];
    
    // Blob URL 정리 (wrapper 구조 지원)
    if (removedFile && removedFile.preview) {
      console.log('🗑️ Cleaning up blob URL for removed file:', removedFile.name);
      URL.revokeObjectURL(removedFile.preview);
    }
    
    onMediaFilesChange(updatedFiles);
    setError(null);
  }, [mediaFiles, onMediaFilesChange, disabled]);

  // 미디어 파일 순서 변경
  const handleMediaReorder = useCallback((fromIndex, toIndex) => {
    if (disabled) return;
    
    const updatedFiles = [...mediaFiles];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    
    onMediaFilesChange(updatedFiles);
  }, [mediaFiles, onMediaFilesChange, disabled]);

  // 모든 미디어 제거
  const handleClearAll = useCallback(() => {
    if (disabled) return;
    
    // 모든 Blob URL 정리
    mediaFiles.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    
    onMediaFilesChange([]);
    setError(null);
  }, [mediaFiles, onMediaFilesChange, disabled]);

  const hasMedia = mediaFiles.length > 0;

  if (isInlineVariant) {
    return (
      <InlineMediaUpload
        mediaFiles={mediaFiles}
        maxFiles={maxFiles}
        disabled={disabled}
        onMediaAdd={handleMediaAdd}
        onMediaRemove={handleMediaRemove}
        error={error}
        setError={setError}
        uploadProgressMap={uploadProgressMap}
        t={t}
        theme={theme}
        showConnectionLine={showConnectionLine}
      />
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      backgroundColor: '#ffffff',
      borderRadius: 2,
      padding: compact ? 1.5 : 2 // 컴팩트 모드에서 패딩 줄임
    }}>
      {/* 헤더 (컴팩트 모드일 때만 표시) */}
      {compact && (
        <Stack 
          direction="row" 
          justifyContent="space-between" 
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Button
            variant="text"
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            startIcon={isExpanded ? <IconChevronUp /> : <IconChevronDown />}
            sx={{ 
              color: theme.palette.text.secondary,
              textTransform: 'none'
            }}
          >
            {hasMedia ? t('postThreads.media.attachMediaWithCount', { count: mediaFiles.length }) : t('postThreads.media.attachMedia')}
          </Button>
          
          {hasMedia && (
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={handleClearAll}
              startIcon={<IconX />}
              sx={{ textTransform: 'none' }}
            >
              {t('common.delete')}
            </Button>
          )}
        </Stack>
      )}

      {/* 에러 알림 */}
      {error && (
        <Alert 
          severity="warning" 
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* 메인 콘텐츠 */}
      <Collapse in={isExpanded}>
        <Stack spacing={2}>
          {/* 드롭존 (파일이 없거나 최대 개수에 도달하지 않은 경우만 표시) */}
          {(!hasMedia || mediaFiles.length < maxFiles) && (
            <MediaDropzone
              onMediaAdd={handleMediaAdd}
              maxFiles={maxFiles - mediaFiles.length}
              disabled={disabled}
            />
          )}

          {/* 미리보기 */}
          {hasMedia && (
            <MediaPreview
              mediaFiles={mediaFiles}
              onRemove={handleMediaRemove}
              onReorder={handleMediaReorder}
              uploadProgressMap={uploadProgressMap}
            />
          )}

          {/* 도움말 텍스트 */}
          {hasMedia && (
            <Typography variant="caption" color="text.secondary">
              {t('postThreads.media.tips.mediaDisplay')}
              {mediaFiles.length > 1 ? t('postThreads.media.tips.carouselDisplay') : t('postThreads.media.tips.singleDisplay')}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default MediaUploadSection;

const InlineMediaUpload = ({
  mediaFiles,
  maxFiles,
  disabled,
  onMediaAdd,
  onMediaRemove,
  error,
  setError,
  uploadProgressMap,
  t,
  theme,
  showConnectionLine = true
}) => {
  const maxRemaining = Math.max(maxFiles - mediaFiles.length, 0);
  const hasMedia = mediaFiles.length > 0;
  const inlineIndentPx = parseFloat(theme.spacing(6));
  const connectionOffset = inlineIndentPx - 18; // 18px is ThreadConnectionLine offset
  const inlinePaddingY = parseFloat(theme.spacing(0.5));

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (disabled) return;

    if (fileRejections && fileRejections.length > 0) {
      const rejectedErrors = fileRejections.map((fileRejection) => {
        const fileName = fileRejection.file.name;
        const errorCodes = fileRejection.errors.map((e) => e.code);

        if (errorCodes.includes('file-invalid-type')) {
          return `${fileName}: ${t('postThreads.media.validation.unsupportedFormat')}`;
        }
        if (errorCodes.includes('file-too-large')) {
          return `${fileName}: ${t('postThreads.media.validation.fileTooLarge')}`;
        }
        if (errorCodes.includes('too-many-files')) {
          return t('postThreads.media.validation.tooManyFiles', { maxFiles });
        }
        return `${fileName}: ${t('postThreads.media.validation.uploadFailed')}`;
      });
      setError(rejectedErrors.join('\n'));
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      setError(null);
      onMediaAdd(acceptedFiles);
    }
  }, [disabled, maxFiles, onMediaAdd, setError, t]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open
  } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov']
    },
    maxFiles: Math.max(maxRemaining, 1),
    disabled,
    multiple: true,
    noClick: true
  });

  const canAddMore = !disabled && maxRemaining > 0;
  const inlineIndent = theme.spacing(6); // avatar(36px) + gap(12px)

  const handleOpenPicker = (event) => {
    event.stopPropagation();
    if (!canAddMore) return;
    open();
  };

  const rootProps = getRootProps();

  const renderPreviewContent = () => {
    if (!mediaFiles.length) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: theme.palette.text.disabled,
            fontSize: '0.8rem'
          }}
        >
          <IconPhotoPlus size={16} />
          <Typography variant="caption" color="text.secondary">
            {t('postThreads.media.attachMedia')}
          </Typography>
        </Box>
      );
    }

    return mediaFiles.map((file, index) => {
      const previewUrl = file?.preview || file?.url || '';
      const progress = uploadProgressMap[index];

      return (
        <Box
          key={file.id || `${file.name}-${index}`}
          sx={{
            position: 'relative',
            width: 48,
            height: 48,
            overflow: 'hidden',
            borderRadius: theme.spacing(1.2),
            border: `1px solid ${theme.palette.grey[200]}`,
            flexShrink: 0
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt={file.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.grey[500],
                fontSize: 12,
                fontWeight: 600
              }}
            >
              {index + 1}
            </Box>
          )}

          {!disabled && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onMediaRemove(index);
              }}
              sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 20,
                height: 20,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.75)'
                }
              }}
            >
              <IconX size={12} />
            </IconButton>
          )}

          {typeof progress === 'number' && progress < 100 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                backgroundColor: 'rgba(0,0,0,0.35)',
                color: '#fff',
                fontSize: 10,
                textAlign: 'center',
                py: 0.2
              }}
            >
              {`${Math.round(progress)}%`}
            </Box>
          )}
        </Box>
      );
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert
          severity="warning"
          onClose={() => setError(null)}
          sx={{ mb: 1.5 }}
        >
          {error}
        </Alert>
      )}

      <Box
        {...rootProps}
        onClick={(event) => {
          rootProps.onClick?.(event);
          if (!canAddMore) return;
          open();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          paddingY: theme.spacing(0.5),
          paddingX: theme.spacing(0.75),
          borderRadius: theme.spacing(6),
          border: `1px solid ${isDragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
          backgroundColor: isDragActive ? theme.palette.action.hover : '#fff',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          cursor: canAddMore ? 'pointer' : 'default',
          minHeight: 48,
          marginLeft: showConnectionLine ? `${inlineIndentPx}px` : 0,
          width: showConnectionLine ? `calc(100% - ${inlineIndentPx}px)` : '100%',
          position: 'relative',
          '&::before': showConnectionLine ? {
            content: '""',
            position: 'absolute',
            left: `-${connectionOffset}px`,
            top: `-${inlinePaddingY}px`,
            bottom: `-${inlinePaddingY}px`,
            width: '2px',
            backgroundColor: theme.palette.grey[300]
          } : undefined
        }}
      >
        <input {...getInputProps()} />
        <Button
          variant="text"
          size="small"
          startIcon={<IconPhotoPlus size={16} />}
          disabled={!canAddMore}
          onClick={handleOpenPicker}
          sx={{
            textTransform: 'none',
            color: canAddMore ? theme.palette.primary.main : theme.palette.text.disabled,
            minWidth: 'auto',
            px: 1,
            fontSize: '0.85rem'
          }}
        >
          {hasMedia
            ? t('postThreads.media.attachMediaWithCount', { count: mediaFiles.length })
            : t('postThreads.media.attachMedia')}
        </Button>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            overflowX: 'auto',
            py: 0
          }}
        >
          {renderPreviewContent()}
        </Box>
      </Box>

      {!canAddMore && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
          {t('postThreads.media.maxFilesExceeded', { maxFiles })}
        </Typography>
      )}
    </Box>
  );
};
